// Logger removed to avoid process.env dependencies in widget bundle

/**
 * Session Event Bus
 * 
 * Provides a centralized communication system between:
 * - Token refresh logic (httpClient.ts)
 * - Session management (useUnifiedSessionManager.ts)
 * - Other session-related components
 * 
 * This prevents direct interference between token refresh and inactivity timers
 */

export type SessionEventType = 
  | 'TOKEN_REFRESH_SUCCESS' 
  | 'TOKEN_REFRESH_FAILURE'
  | 'TOKEN_EXPIRED'
  | 'SESSION_ACTIVITY' 
  | 'SESSION_WARNING'
  | 'SESSION_EXPIRED'
  | 'SESSION_EXTENDED';

export interface SessionEvent {
  type: SessionEventType;
  timestamp: number;
  data?: any;
  source?: string; // Which component triggered the event
}

export type SessionEventListener = (event: SessionEvent) => void;

class SessionEventBus {
  private listeners: Map<SessionEventType, Set<SessionEventListener>> = new Map();
  private eventHistory: SessionEvent[] = [];
  private readonly MAX_HISTORY_SIZE = 100;

  /**
   * Subscribe to a specific event type
   */
  subscribe(eventType: SessionEventType, listener: SessionEventListener): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    
    this.listeners.get(eventType)!.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(eventType)?.delete(listener);
    };
  }

  /**
   * Emit an event to all subscribers
   */
  emit(type: SessionEventType, data?: any, source?: string): void {
    const event: SessionEvent = {
      type,
      timestamp: Date.now(),
      data,
      source: source || 'unknown'
    };

    // Add to history
    this.eventHistory.push(event);
    if (this.eventHistory.length > this.MAX_HISTORY_SIZE) {
      this.eventHistory.shift();
    }

    // Debug logging
    //if (process.env.NODE_ENV === 'development') {
    //  console.info(`🔄 SessionEventBus: ${type}`, {
    //    source,
    //    data,
    //    timestamp: new Date(event.timestamp).toISOString()
    //  });
    //}

    // Notify all listeners
    const listeners = this.listeners.get(type);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          console.error(`Error in session event listener for ${type}:`, error);
        }
      });
    }
  }

  /**
   * Get recent event history
   */
  getEventHistory(eventType?: SessionEventType, limit = 10): SessionEvent[] {
    let events = this.eventHistory;
    
    if (eventType) {
      events = events.filter(event => event.type === eventType);
    }
    
    return events.slice(-limit);
  }

  /**
   * Clear event history
   */
  clearHistory(): void {
    this.eventHistory = [];
  }

  /**
   * Get the last event of a specific type
   */
  getLastEvent(eventType: SessionEventType): SessionEvent | null {
    for (let i = this.eventHistory.length - 1; i >= 0; i--) {
      if (this.eventHistory[i].type === eventType) {
        return this.eventHistory[i];
      }
    }
    return null;
  }

  /**
   * Check if an event occurred within a time window
   */
  hasRecentEvent(eventType: SessionEventType, withinMs: number): boolean {
    const lastEvent = this.getLastEvent(eventType);
    if (!lastEvent) return false;
    
    const timeSince = Date.now() - lastEvent.timestamp;
    return timeSince <= withinMs;
  }
}

// Create and export a singleton instance
export const sessionEventBus = new SessionEventBus();

// Helper functions for common patterns
export const emitTokenRefresh = (success: boolean, source = 'httpClient') => {
  sessionEventBus.emit(
    success ? 'TOKEN_REFRESH_SUCCESS' : 'TOKEN_REFRESH_FAILURE',
    { success },
    source
  );
};

export const emitSessionActivity = (activityType: string, source = 'sessionManager') => {
  sessionEventBus.emit(
    'SESSION_ACTIVITY',
    { activityType },
    source
  );
};

export const emitSessionWarning = (timeLeft: number, source = 'sessionManager') => {
  sessionEventBus.emit(
    'SESSION_WARNING',
    { timeLeft },
    source
  );
};

export const emitSessionExpired = (reason: string, source = 'sessionManager') => {
  sessionEventBus.emit(
    'SESSION_EXPIRED',
    { reason },
    source
  );
};

export const emitSessionExtended = (source = 'user') => {
  sessionEventBus.emit(
    'SESSION_EXTENDED',
    {},
    source
  );
};